import { Box, makeStyles } from '@material-ui/core';
import { useParams } from '@reach/router';
import React, { useEffect } from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import PageContent from '../../../components/Layout/PageContent';
import RawReportColumnSelectionModal from '../../../components/Modals/rawReportColumnSelectionModal';
import { BenchmarkLinkState } from '../../../components/TableGrid/Renderers/LinkCellRenderer/BenchmarkLinkCellRenderer';
import { TableGridApiContextProvider } from '../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import ActionBox from '../../../components/TableGrid/TableGridToolbar/ActionBox/ActionBox';
import { ActionLink } from '../../../components/TableGrid/TableGridToolbar/ActionBox/ActionBox.type';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import { api } from '../../../core/api';
import { SearchReportTypes } from '../../../core/api/search';
import { CONSTANTS } from '../../../core/config/constants';
import { FontSizes } from '../../../core/config/mainTheme';
import { isTrialAccess } from '../../../core/utils/access.utils';
import { toCssValue } from '../../../core/utils/theme.utils';
import { CompanySearchResultEntry } from '../../../generated/model/models';
import { useAppGoogleReCaptcha } from '../../../hooks/useAppGoogleReCaptcha';
import { ROUTES } from '../../../routes';
import { useCompaniesDispatch, useCompaniesState } from '../shared/CompanyContext/CompanyContext';
import { CompanyContextActionTypes } from '../shared/CompanyContext/CompanyContext.types';
import { useErrorsDispatch } from '../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../shared/ErrorContext/ErrorContext.types';
import { useFilterState } from '../shared/FinancialDataContext/FinancialDataContext';
import ThemeBreadcrumbs from './BreadCrumbs';
import { BreadCrumbLink } from './BreadCrumbs/BreadCrumbs';
import CompanyBlock from './CompanyBlock';
import SearchPanel from './SearchPanel';

interface OwnProps {
  path: string;
}

const getActionItems = (
  companyId: string,
  downloadIsForbidden: boolean,
  searchId?: string,
): ActionLink[] => [
  {
    title: 'companies:downloadReportRaw',
    snackbarReportText: 'companies:snackbarReportRawAndMarketingText',
    onClick: async () => {
      ReactGA.event({ category: 'COMPANY', action: 'LIST_REPORT' });
      await api.search.getReportByCompanyId(SearchReportTypes.RAW, companyId, searchId);
    },
    isNotActive: false,
    isHidden: true,
  },
  {
    title: 'companies:downloadReportRawSelected',
    snackbarReportText: 'companies:snackbarReportRawAndMarketingText',
    isNotActive: false,
    isHidden: downloadIsForbidden,
    customDialog: (
      open: boolean,
      handleClose: () => void,
      handleConfirmOk: (onClick: () => Promise<void>) => void,
    ) => (
      <RawReportColumnSelectionModal
        open={open}
        handleClose={handleClose}
        handleConfirmOk={handleConfirmOk}
        companyId={companyId}
        searchId={searchId}
        // searchVisibleCols={searchVisibleCols}
      />
    ),
  },
  {
    title: 'companies:downloadReportMarketing',
    snackbarReportText: 'companies:snackbarReportRawAndMarketingText',
    onClick: async () => {
      ReactGA.event({ category: 'COMPANY', action: 'STRUCTURED_REPORT' });
      await api.search.getReportByCompanyId(SearchReportTypes.MARKETING, companyId, searchId);
    },
    isNotActive: false,
    isHidden: downloadIsForbidden,
  },
  {
    title: 'companies:downloadReportBoth',
    snackbarReportText: 'companies:snackbarReportBothText',
    onClick: async () => {
      ReactGA.event({ category: 'COMPANY', action: 'BOTH_REPORTS' });
      await api.search.getBothReportsById(companyId, searchId);
    },
    isNotActive: false,
    isHidden: true,
  },
];

const CompanyPage: React.FC<OwnProps> = () => {
  const classes = useStyles();
  const { requestFiltersData, isRefinement } = useFilterState();
  const { companyUid, benchmarkId, dataset: datasetParam, dbVersion: dbVersionParam } = useParams<{
    companyUid?: string;
    benchmarkId?: string;
    dataset?: string;
    dbVersion?: string;
  }>();
  const { companies, company, searchId, dataset } = useCompaniesState();
  const dispatch = useCompaniesDispatch();
  const dispatchErrors = useErrorsDispatch();
  const { t } = useTranslation();
  const companyName = company?.companyInfo?.name ?? CONSTANTS.UNKNOWN_NAME;
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const { user } = useAuth();
  const isDownloadForbidden = isTrialAccess(user, dataset);

  const linkState: BenchmarkLinkState = {
    switchToRefinementTab: isRefinement,
  };

  const breadCrumbs: BreadCrumbLink[] = [
    {
      path: isRefinement
        ? generatePath(ROUTES.benchmarking.benchmark, {
            benchmarkId: benchmarkId,
          })
        : generatePath(ROUTES.financialData.page),
      text: t('app:navTab:companies'),
      linkState,
    },
    {
      text: companyName,
    },
  ];

  useEffect(() => {
    if (datasetParam || dbVersionParam) {
      dispatch({
        type: CompanyContextActionTypes.SetDatasetInfo,
        payload: { dataset: datasetParam, dbVersion: dbVersionParam },
      });
    }
  }, [datasetParam, dbVersionParam, dispatch]);

  useEffect(() => {
    if (companyUid) {
      const filteredCompany = companies.find(
        (companyObject: CompanySearchResultEntry) => companyUid === companyObject.companyInfo?.id,
      );
      ReactGA.pageview(window.location.pathname);
      if (filteredCompany && companyUid === filteredCompany.companyInfo?.id) {
        dispatch({
          type: CompanyContextActionTypes.SetCompany,
          payload: filteredCompany,
        });
      } else {
        const fetchCompanyById = async () => {
          const recaptchaToken = await executeRecaptcha?.('search');
          try {
            const data = await api.companySearch.companySearchSingle(companyUid, recaptchaToken!);
            if (data) {
              dispatch({
                type: CompanyContextActionTypes.SetCompany,
                payload: data.data,
              });
            }
          } catch (error) {
            dispatchErrors({
              type: ErrorContextActionTypes.SetError,
              payload: { error },
            });
          }
        };
        fetchCompanyById();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyUid, dispatch, companies, executeRecaptcha]);

  return (
    <Box>
      <div className={classes.breadcrumbContainer}>
        <ThemeBreadcrumbs items={breadCrumbs} />
        {company?.companyInfo?.id && (
          <ActionBox
            buttonTitle={t('app:download')}
            actionItems={getActionItems(company?.companyInfo.id, isDownloadForbidden, searchId)}
          />
        )}
      </div>
      <PageContent>
        {Object.keys(requestFiltersData).length > 0 && (
          <TableGridApiContextProvider>
            <SearchPanel />
          </TableGridApiContextProvider>
        )}
        {company && (
          <CompanyBlock
            companyBlockStyle={
              companies.length > 0 ? classes.companyBlock : classes.companyBlockWithoutNavigation
            }
          />
        )}
      </PageContent>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  breadcrumbContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.primary.dark,
    display: 'flex',
    flex: 1,
    padding: toCssValue([theme.spacing(0), theme.spacing(6)]),
    '& .MuiTypography-root': {
      fontSize: FontSizes.size.headerSize,
      color: theme.palette.primary.contrastText,

      '&:hover': {
        cursor: 'pointer',
      },
    },
    '& .MuiButtonBase-root': {
      height: '44px',
      boxShadow: 'none',
    },
  },
  companyBlock: {
    width: 'calc(100% - 360px)',
    paddingRight: theme.spacing(4),
    //     userSelect: 'none',
  },
  companyBlockWithoutNavigation: {
    width: 'calc(100%)',
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    //     userSelect: 'none',
  },
}));

export default CompanyPage;
